import React from 'react';
import { Text, Linking, Alert, StyleSheet, TextStyle } from 'react-native';

interface LinkifiedTextProps {
  children: string;
  style?: any;
  linkStyle?: any;
}

type Segment = {
  type: 'text' | 'link' | 'bold' | 'italic' | 'bolditalic';
  content: string;
  linkType?: 'url' | 'phone' | 'email';
};

export default function LinkifiedText({ children, style, linkStyle }: LinkifiedTextProps) {
  const text = children || '';

  const patterns = {
    url: /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|(?:www\.)?(?:zoom\.us|meet\.google\.com|teams\.microsoft\.com|webex\.com)[^\s<>"{}|\\^`\[\]]*|(?:www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]\.[a-zA-Z]{2,}(?:\/[^\s<>"{}|\\^`\[\]]*)?)/gi,
    phone: /(?:\+?55\s?)?(?:\(?(?:0?[1-9]{2})\)?\s?)?(?:[6-9][0-9]{3,4}-?[0-9]{4})/g,
    email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g
  };

  const handleLinkPress = async (url: string, type: 'url' | 'phone' | 'email') => {
    try {
      let finalUrl = url;

      switch (type) {
        case 'url':
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            finalUrl = `https://${url}`;
          }
          break;
        case 'phone':
          finalUrl = `tel:${url.replace(/\D/g, '')}`;
          break;
        case 'email':
          finalUrl = `mailto:${url}`;
          break;
      }

      const canOpen = await Linking.canOpenURL(finalUrl);
      if (canOpen) {
        await Linking.openURL(finalUrl);
      } else {
        Alert.alert('Erro', 'Não foi possível abrir este link');
      }
    } catch (error) {
      Alert.alert('Erro', 'Não foi possível abrir este link');
    }
  };

  const parseMarkdown = (input: string): Segment[] => {
    const segments: Segment[] = [];
    const mdPattern = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|__(.+?)__|_(.+?)_)/g;
    let lastIndex = 0;
    let match;

    while ((match = mdPattern.exec(input)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ type: 'text', content: input.substring(lastIndex, match.index) });
      }

      if (match[2]) {
        segments.push({ type: 'bolditalic', content: match[2] });
      } else if (match[3]) {
        segments.push({ type: 'bold', content: match[3] });
      } else if (match[4]) {
        segments.push({ type: 'italic', content: match[4] });
      } else if (match[5]) {
        segments.push({ type: 'bold', content: match[5] });
      } else if (match[6]) {
        segments.push({ type: 'italic', content: match[6] });
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < input.length) {
      segments.push({ type: 'text', content: input.substring(lastIndex) });
    }

    return segments;
  };

  const renderTextWithLinks = () => {
    const allMatches: Array<{
      match: string;
      index: number;
      end: number;
      type: 'url' | 'phone' | 'email';
    }> = [];

    const findMatches = (pattern: RegExp, type: 'url' | 'phone' | 'email') => {
      const regex = new RegExp(pattern.source, pattern.flags);
      let match;
      while ((match = regex.exec(text)) !== null) {
        allMatches.push({
          match: match[0],
          index: match.index,
          end: match.index + match[0].length,
          type: type
        });
        if (match.index === regex.lastIndex) {
          regex.lastIndex++;
        }
      }
    };

    findMatches(patterns.url, 'url');
    findMatches(patterns.phone, 'phone');
    findMatches(patterns.email, 'email');

    allMatches.sort((a, b) => a.index - b.index);

    const cleanMatches: typeof allMatches = [];
    for (let i = 0; i < allMatches.length; i++) {
      const current = allMatches[i];
      const isOverlapping = cleanMatches.some(existing =>
        (current.index >= existing.index && current.index < existing.end) ||
        (current.end > existing.index && current.end <= existing.end)
      );
      if (!isOverlapping) {
        cleanMatches.push(current);
      }
    }

    const rawSegments: Segment[] = [];
    let lastIndex = 0;

    cleanMatches.forEach((linkMatch) => {
      if (linkMatch.index > lastIndex) {
        rawSegments.push({
          type: 'text',
          content: text.substring(lastIndex, linkMatch.index)
        });
      }
      rawSegments.push({
        type: 'link',
        content: linkMatch.match,
        linkType: linkMatch.type
      });
      lastIndex = linkMatch.end;
    });

    if (lastIndex < text.length) {
      rawSegments.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }

    const finalSegments: Segment[] = [];
    for (const seg of rawSegments) {
      if (seg.type === 'text') {
        finalSegments.push(...parseMarkdown(seg.content));
      } else {
        finalSegments.push(seg);
      }
    }

    if (finalSegments.length === 0) {
      return <Text style={style}>{text}</Text>;
    }

    return (
      <Text style={style}>
        {finalSegments.map((segment, index) => {
          switch (segment.type) {
            case 'link':
              return (
                <Text
                  key={index}
                  style={[styles.link, linkStyle]}
                  onPress={() => handleLinkPress(segment.content, segment.linkType as 'url' | 'phone' | 'email')}
                >
                  {segment.content}
                </Text>
              );
            case 'bold':
              return (
                <Text key={index} style={styles.bold}>
                  {segment.content}
                </Text>
              );
            case 'italic':
              return (
                <Text key={index} style={styles.italic}>
                  {segment.content}
                </Text>
              );
            case 'bolditalic':
              return (
                <Text key={index} style={styles.boldItalic}>
                  {segment.content}
                </Text>
              );
            default:
              return <Text key={index}>{segment.content}</Text>;
          }
        })}
      </Text>
    );
  };

  return renderTextWithLinks();
}

const styles = StyleSheet.create({
  link: {
    color: '#015480',
    textDecorationLine: 'underline',
    fontWeight: '500',
  },
  bold: {
    fontWeight: '700',
  },
  italic: {
    fontStyle: 'italic',
  },
  boldItalic: {
    fontWeight: '700',
    fontStyle: 'italic',
  },
});
